import { useState, useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";

import { CustomEmojiAddModal } from "./CustomEmojiAddModal";
import { CustomEmojiList } from "./CustomEmojiList";

import { confirmAsync } from "~/client/components/ConfirmDialog";
import { KibelaSymbol } from "~/client/components/shared/kibela_symbol";
import { Toast } from "~/client/components/shared/toast";
import type { CreateCustomEmojiMutation } from "~/client/generated/schema";
import { CreateCustomEmojiDocument, DeleteCustomEmojiDocument } from "~/client/generated/schema";
import { client } from "~/client/utils/kibelaClient2";
import { basename } from "~/client/utils/path";

type CustomEmoji = CreateCustomEmojiMutation["createCustomEmoji"]["customEmoji"];

interface Props {
  intl: any;
  customEmojis: Array<CustomEmoji>;
}

export const CustomEmojiContainer: React.FC<Props> = (props) => {
  const [customEmojis, setCustomEmojis] = useState<Array<CustomEmoji>>(props.customEmojis);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [emojiCode, setEmojiCode] = useState<string>("");
  const [imageDataUrl, setImageDataUrl] = useState<string>("");
  const [imageFileName, setImageFileName] = useState<string>("");
  const [waitingMutation, setWaitingMutation] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const toastRef = useRef<Toast | null>(null);

  useEffect(() => {
    if (toastRef.current && errorMessage) {
      toastRef.current.showToast({ error: errorMessage });
    }
  }, [errorMessage]);

  const isValidEmojiCode = (emojiCode: string): boolean => {
    // NOTE: Also defined in app/models/custom_emoji.rb
    return /^[A-Za-z0-9_\-]+$/.exec(emojiCode) !== null;
  };

  const getEmojiCodeFromFileName = (fileName: string) => {
    const emojiCode = basename(fileName, ".*");
    if (isValidEmojiCode(emojiCode)) {
      return emojiCode;
    } else {
      return "";
    }
  };

  const handleChangeImage = (e) => {
    if (!(e.target.files && e.target.files[0])) {
      return;
    }
    const file = e.target.files[0];

    if (file.size > 1024 * 1024) {
      setErrorMessage(props.intl.formatMessage({ id: "customEmoji.imageSizeOver" }));
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = (event) => {
      if (event.target && event.target.result) {
        setImageDataUrl(event.target.result as string);
        setImageFileName(file.name);
        if (!emojiCode) {
          setEmojiCode(getEmojiCodeFromFileName(file.name));
        }
      }
    };
    fileReader.readAsDataURL(file);
  };

  const initializeModalInputs = () => {
    setEmojiCode("");
    setImageDataUrl("");
    setImageFileName("");
  };

  const handleCreateCustomEmoji = async (ev: React.FormEvent) => {
    ev.preventDefault();

    if (waitingMutation) {
      return;
    }
    setWaitingMutation(true);

    let result;
    try {
      result = await client.mutate({
        mutation: CreateCustomEmojiDocument,
        variables: { input: { emojiCode, imageDataUrl } },
      });
    } catch (reason) {
      setErrorMessage(reason.message);
      return Promise.reject(reason);
    } finally {
      setWaitingMutation(false);
    }

    toastRef.current!.showToast({ success: props.intl.formatMessage({ id: "customEmoji.created" }) });
    const newCustomEmoji = result.data.createCustomEmoji.customEmoji;
    setCustomEmojis([newCustomEmoji, ...customEmojis]);
    initializeModalInputs();
    setIsAddModalOpen(false);

    return Promise.resolve();
  };

  const handleDeleteCustomEmoji = async (customEmojiId) => {
    const isConfirmed = await confirmAsync({
      intl: props.intl,
      title: <FormattedMessage id="customEmoji.deleteConfirmTitle" />,
      content: <FormattedMessage id="customEmoji.deleteConfirmMessage" />,
      okButtonClassName: "is-button-warning",
    });

    if (!isConfirmed) {
      return Promise.resolve();
    }

    if (waitingMutation) {
      return;
    }
    setWaitingMutation(true);

    try {
      await client.mutate({
        mutation: DeleteCustomEmojiDocument,
        variables: { input: { customEmojiId } },
      });
    } catch (reason) {
      setErrorMessage(reason.message);
      return Promise.reject(reason);
    } finally {
      setWaitingMutation(false);
    }

    toastRef.current!.showToast({ success: props.intl.formatMessage({ id: "customEmoji.deleted" }) });
    setCustomEmojis(customEmojis.filter((x) => x.id !== customEmojiId));

    return Promise.resolve();
  };

  return (
    <div className="customEmoji">
      <Toast ref={toastRef}></Toast>

      <button
        type="button"
        className="button is-attentionBorder is-button-small settings-rightButton"
        onClick={() => setIsAddModalOpen(true)}
      >
        <KibelaSymbol id="plus" />
        <FormattedMessage id="customEmoji.addition" />
      </button>

      <CustomEmojiAddModal
        isAddModalOpen={isAddModalOpen}
        isSubmitting={waitingMutation}
        emojiCode={emojiCode}
        imageDataUrl={imageDataUrl}
        imageFileName={imageFileName}
        setIsAddModalOpen={setIsAddModalOpen}
        setEmojiCode={setEmojiCode}
        onChangeImage={handleChangeImage}
        onSubmit={handleCreateCustomEmoji}
      />

      <CustomEmojiList intl={props.intl} customEmojis={customEmojis} onClickDelete={handleDeleteCustomEmoji} />
    </div>
  );
};
